import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

/**
 * Button — primary interactive control.
 *
 * Visual language updated for the bento redesign:
 *   • ``primary`` is a brand-blue gradient (#1B18FF → #4340FF) with a
 *     soft brand-glow shadow. Hover deepens the gradient + lifts the
 *     glow. Active scales to 0.97 for the tactile press feel.
 *   • ``secondary`` and ``outline`` keep their flat look but pick up
 *     the same scale-press animation so all buttons feel consistent.
 *   • ``ghost`` and ``danger`` follow the same animation + 12px radius.
 *
 * The radius bumps from 8px to 12px to match the bento language
 * without becoming cartoonish on small buttons.
 */

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

const variantStyles: Record<Variant, string> = {
  // Brand-blue gradient + glow shadow on primary. The hover state
  // shifts to a slightly darker gradient (the brand-spec hover) AND
  // lifts the shadow — most buttons won't need hand-tuned hover.
  primary: cn(
    "text-white",
    "bg-[linear-gradient(135deg,#1B18FF_0%,#4340FF_100%)]",
    "shadow-[0_2px_10px_rgba(27,24,255,0.25)]",
    "hover:bg-[linear-gradient(135deg,#1714CC_0%,#1B18FF_100%)]",
    "hover:shadow-[0_4px_18px_rgba(27,24,255,0.4)]",
    "hover:-translate-y-px",
  ),
  secondary:
    "bg-surface text-text-primary border border-border hover:bg-surface-muted hover:border-text-muted/30",
  ghost:
    "text-text-muted hover:text-text-primary hover:bg-surface-muted",
  danger:
    "bg-danger text-white shadow-[0_2px_10px_rgba(239,68,68,0.25)] hover:bg-red-600 hover:shadow-[0_4px_18px_rgba(239,68,68,0.4)] hover:-translate-y-px",
  outline:
    "border border-accent text-accent hover:bg-accent/10",
};

const sizeStyles: Record<Size, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-2.5 text-base",
};

/** Shared button classes — exported so router Links can look like buttons
 *  without double-wrapping (<Button asChild> complicates the DOM more
 *  than it helps for a <Link> inside EmptyState's CTA).
 */
export function buttonClasses(
  variant: Variant = "primary",
  size: Size = "md",
  extra?: string,
) {
  return cn(
    "inline-flex items-center justify-center gap-2 rounded-xl font-medium",
    // Faster transition than the old 150ms colors-only — we now also
    // animate transform + box-shadow for the lift effect.
    "transition-all duration-150 ease-out",
    "active:scale-[0.97] active:translate-y-0",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none",
    variantStyles[variant],
    sizeStyles[size],
    extra,
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      className,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={buttonClasses(variant, size, className)}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        aria-disabled={disabled || isLoading || undefined}
        {...props}
      >
        {isLoading && (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
